import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { AttendanceStatusBadge } from "@/components/StatusBadge";
import { AttendanceMap } from "@/components/AttendanceMap";
import {
  Search, Download, Eye, Flag, MapPin, Clock, User, Building,
  Navigation, AlertTriangle, CheckCircle2, FileText, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import {
  attendanceApi, AttendanceItem, AttendanceDetail, AttendanceSummary,
  AttendanceSessionItem, ListAttendanceQuery, AttendanceStatus,
  GpsStatus, GeofenceStatus, SessionCloseReason,
  WorkMode,
} from "@/lib/attendance-api";
import { sitesApi, SiteSummary } from "@/lib/sites-api";
import { departmentsApi, Department } from "@/lib/departments-api";
import { departmentOptions, pickedIds, pickedLabel, siteOptions } from "@/lib/picker-options";
import { SearchMultiSelect } from "@/components/SearchMultiSelect";
import { ApiError } from "@/lib/api";
import { DateRange, formatRangeLabel } from "@/lib/date-range";
import { DateRangeFilter } from "@/components/DateRangeFilter";
import { ExportMenu } from "@/components/ExportMenu";
import type { ExportTable } from "@/lib/export";
import { formatTimeIn, weekdayName } from "@/lib/datetime";
import { formatDuration, hoursWorkedSummary, shortDayReasons } from "@/lib/work-hours";
import { WorkedHours } from "@/components/WorkedHours";
import { useTenantTimezone } from "@/hooks/useTenantTimezone";
import { useAnchoredDateRange } from "@/hooks/useAnchoredDateRange";
import { useDebouncedSearch } from "@/hooks/useDebouncedSearch";
import { usePage } from "@/hooks/usePage";
import { ListPager } from "@/components/ListPager";
import { fetchAllPages } from "@/lib/paging";
import type { AttendanceStatus as UiAttendanceStatus } from "@/data/mock";

const STATUS_LABELS: Record<AttendanceStatus, UiAttendanceStatus> = {
  PRESENT: "Present",
  CHECKED_IN: "Checked In",
  CHECKED_OUT: "Checked Out",
  PARTIAL: "Partial",
  EXCEPTION: "Exception",
  MISSED: "Missed",
  OUTSIDE_GEOFENCE: "Outside Geofence",
  PENDING_REVIEW: "Pending Review",
};

const STATUS_OPTIONS: { value: AttendanceStatus; label: string }[] = [
  { value: "PRESENT", label: "Present" },
  { value: "CHECKED_IN", label: "Checked In" },
  { value: "CHECKED_OUT", label: "Checked Out" },
  { value: "PARTIAL", label: "Partial" },
  { value: "EXCEPTION", label: "Exception" },
  { value: "MISSED", label: "Missed" },
  { value: "OUTSIDE_GEOFENCE", label: "Outside Geofence" },
  { value: "PENDING_REVIEW", label: "Pending Review" },
];

const GPS_LABELS: Record<GpsStatus, string> = { VALID: "Valid", INVALID: "Invalid", UNKNOWN: "Unknown" };
const GEOFENCE_LABELS: Record<GeofenceStatus, string> = {
  INSIDE: "Inside", OUTSIDE: "Outside", NOT_APPLICABLE: "N/A",
};

/**
 * Records per page. The monitor used to fetch one page of 200 and show it as
 * the day — at 1,824 staff, most of a day could not be reached (2026-10-29).
 */
const PAGE_SIZE = 50;

function formatRangeFilename(r: DateRange): string {
  return r.from === r.to ? r.from : `${r.from}_to_${r.to}`;
}

/** Check-in/out clock time in the tenant's timezone (never the viewer's). */
/**
 * The export's columns, in order. HR can leave any of them out (FEAT-2026-029);
 * "Week" is the day name, which KPTM's next system expects (UAT MON-03).
 */
const EXPORT_HEADERS = [
  "Employee", "Code", "Department", "Site", "Date", "Week",
  "Check-in", "Check-out", "Hours", "Short day", "GPS", "Geofence", "Status", "Mode", "Exception", "Remarks",
];

function formatTime(iso: string | null, tz: string): string | null {
  return formatTimeIn(iso, tz, "") || null;
}

const Attendance = () => {
  const tz = useTenantTimezone();
  const { range, setRange, preset, selectPreset } = useAnchoredDateRange("today");
  const { search, setSearch, debouncedSearch } = useDebouncedSearch();
  // Several of each may be picked (FEAT-2026-030); empty means all.
  const [siteFilter, setSiteFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<"all" | AttendanceStatus>("all");
  const [modeFilter, setModeFilter] = useState<"all" | WorkMode>("all");
  const [deptFilter, setDeptFilter] = useState<string[]>([]);
  const [exceptionsOnly, setExceptionsOnly] = useState(false);
  const [assignedToMe, setAssignedToMe] = useState(false);

  const [sites, setSites] = useState<SiteSummary[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const siteChoices = useMemo(() => siteOptions(sites), [sites]);
  const deptChoices = useMemo(() => departmentOptions(departments, { includesSubunits: true }), [departments]);
  const [records, setRecords] = useState<AttendanceItem[]>([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState<AttendanceSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [reloadKey, setReloadKey] = useState(0);

  const [active, setActive] = useState<AttendanceItem | null>(null);
  const [activeDetail, setActiveDetail] = useState<AttendanceDetail | null>(null);
  const [activeLoading, setActiveLoading] = useState(false);
  const [flagReason, setFlagReason] = useState("");
  const [flagging, setFlagging] = useState(false);

  useEffect(() => {
    sitesApi.list({ pageSize: 200 }).then((r) => setSites(r.data)).catch(() => { /* non-fatal */ });
    departmentsApi.listAll().then(setDepartments).catch(() => { /* non-fatal */ });
  }, []);

  // Single-day vs range — the backend supports both via the same DTO.
  // Sending `date` for a single day keeps the request shape identical to
  // the legacy contract; for a range we send `dateFrom`/`dateTo`.
  const dateParams = useMemo(
    () => (range.from === range.to ? { date: range.from } : { dateFrom: range.from, dateTo: range.to }),
    [range.from, range.to],
  );

  /** Every filter on screen — the list, its paging and the export all use it. */
  const filters = useMemo<ListAttendanceQuery>(() => ({
    ...dateParams,
    search: debouncedSearch || undefined,
    siteIds: pickedIds(siteFilter),
    departmentIds: pickedIds(deptFilter),
    status: statusFilter === "all" ? undefined : statusFilter,
    workMode: modeFilter === "all" ? undefined : modeFilter,
    exceptionsOnly: exceptionsOnly || undefined,
    assignedToMe: assignedToMe || undefined,
  }), [dateParams, debouncedSearch, siteFilter, deptFilter, statusFilter, modeFilter, exceptionsOnly, assignedToMe]);
  // Any filter change goes back to page 1.
  const [page, setPage] = usePage(JSON.stringify(filters));

  // Range guard: if the user types `to < from`, skip the fetch and let them
  // fix it. The backend would 400 anyway; failing fast keeps the UI from
  // flashing an error toast on every keystroke.
  const rangeValid = range.from <= range.to;

  useEffect(() => {
    if (!rangeValid) return;
    let cancelled = false;
    setLoading(true);
    attendanceApi
      .list({ ...filters, page, pageSize: PAGE_SIZE })
      .then((list) => {
        if (cancelled) return;
        setRecords(list.data);
        setTotal(list.total);
      })
      .catch((e) => {
        if (cancelled) return;
        toast.error(e instanceof ApiError ? e.message : "Failed to load attendance");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [filters, page, rangeValid, reloadKey]);

  // The cards cover the day (or range) at the chosen site — not one page.
  useEffect(() => {
    if (!rangeValid) return;
    let cancelled = false;
    attendanceApi
      .summary({ ...dateParams, siteIds: filters.siteIds, assignedToMe: filters.assignedToMe })
      .then((sum) => { if (!cancelled) setSummary(sum); })
      .catch(() => { /* non-fatal — the list reports its own errors */ });
    return () => { cancelled = true; };
  }, [dateParams, filters.siteIds, filters.assignedToMe, rangeValid, reloadKey]);

  const openDetail = (r: AttendanceItem) => {
    setActive(r);
    setActiveDetail(null);
    setFlagReason(r.flaggedReason ?? "");
    setActiveLoading(true);
    attendanceApi
      .get(r.id)
      .then((d) => setActiveDetail(d))
      .catch((e) => {
        toast.error(e instanceof ApiError ? e.message : "Could not load attendance detail");
      })
      .finally(() => setActiveLoading(false));
  };

  const triggerReload = () => setReloadKey((k) => k + 1);

  const submitFlag = async () => {
    if (!active) return;
    setFlagging(true);
    try {
      await attendanceApi.flag(active.id, { reason: flagReason.trim() || undefined });
      toast.success(`Flagged ${active.employeeName} for review`);
      setActive(null);
      triggerReload();
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : "Failed to flag record");
    } finally {
      setFlagging(false);
    }
  };

  /**
   * Every record the filters match — fetched across pages at click time, not
   * just the page on screen — in whichever format the admin picks.
   */
  const buildExportTable = async (): Promise<ExportTable> => {
    const all = await fetchAllPages((p, pageSize) => attendanceApi.list({ ...filters, page: p, pageSize }));
    return {
      filename: `attendance-${formatRangeFilename(range)}`,
      title: "Attendance records",
      meta: {
        Period: formatRangeLabel(range),
        Site: pickedLabel(siteFilter, siteChoices, "All sites"),
        Department: pickedLabel(deptFilter, deptChoices, "All departments"),
        Records: String(all.length),
      },
      headers: EXPORT_HEADERS,
      rows: all.map((r) => [
        r.employeeName, r.employeeEmpCode, r.employeeDepartment ?? "",
        r.siteName ?? "", r.date, weekdayName(r.date),
        formatTime(r.checkInAt, tz) ?? "", formatTime(r.checkOutAt, tz) ?? "",
        r.checkOutAt && r.durationMinutes != null ? formatDuration(r.durationMinutes) : "",
        shortDayReasons(r).join("; "),
        GPS_LABELS[r.gpsStatus], GEOFENCE_LABELS[r.geofenceStatus],
        STATUS_LABELS[r.status], r.workMode, r.isException ? "Yes" : "No",
        (r.flaggedReason ?? r.remarks ?? "").replace(/\n/g, " "),
      ]),
    };
  };

  return (
    <div className="space-y-5">
      {/* KPI cards */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          label="Checked-in"
          value={summary?.checkedIn ?? 0}
          hint={
            summary && (summary.workModes.wfh > 0 || summary.workModes.wfa > 0)
              ? `${summary.workModes.wfo} WFO / ${summary.workModes.wfh} WFH / ${summary.workModes.wfa} WFA`
              : undefined
          }
        />
        <KpiCard label="Checked-out" value={summary?.checkedOut ?? 0} tone="primary" />
        <KpiCard label="Exceptions" value={summary?.exceptions ?? 0} tone="destructive" />
        <KpiCard label="Pending review" value={summary?.pendingReview ?? 0} tone="warning" />
      </div>

      {/* Filters */}
      <Card className="p-4 space-y-3">
        {/* Date range — the one shared control (see DateRangeFilter). */}
        <div className="flex flex-wrap items-end gap-3">
          <DateRangeFilter value={range} preset={preset} onSelectPreset={selectPreset} onChange={setRange} />
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[220px]">
            <Label className="text-xs text-muted-foreground">Search</Label>
            <div className="relative mt-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or code…"
                className="pl-9 h-9"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Site</Label>
            <SearchMultiSelect
              label="Site"
              values={siteFilter}
              onChange={setSiteFilter}
              options={siteChoices}
              allLabel="All sites"
              noun={{ one: "site", many: "sites" }}
              className="mt-1 h-9 w-[200px]"
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Department</Label>
            <SearchMultiSelect
              label="Department"
              values={deptFilter}
              onChange={setDeptFilter}
              options={deptChoices}
              allLabel="All departments"
              noun={{ one: "department", many: "departments" }}
              className="mt-1 h-9 w-[240px]"
            />
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Status</Label>
            <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as "all" | AttendanceStatus)}>
              <SelectTrigger className="mt-1 h-9 w-[180px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {STATUS_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Mode</Label>
            <Select value={modeFilter} onValueChange={(v) => setModeFilter(v as "all" | WorkMode)}>
              <SelectTrigger className="mt-1 h-9 w-[130px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All modes</SelectItem>
                <SelectItem value="WFO">WFO</SelectItem>
                <SelectItem value="WFH">WFH</SelectItem>
                <SelectItem value="WFA">WFA</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 px-2 h-9">
            <Switch id="excpt" checked={exceptionsOnly} onCheckedChange={setExceptionsOnly} />
            <Label htmlFor="excpt" className="text-xs">Exceptions only</Label>
          </div>
          <div className="flex items-center gap-2 px-2 h-9">
            <Switch id="mine" checked={assignedToMe} onCheckedChange={setAssignedToMe} />
            <Label htmlFor="mine" className="text-xs">Assigned to me</Label>
          </div>
          <div className="ml-auto">
            <ExportMenu
              build={buildExportTable}
              disabled={loading || total === 0}
              columns={{ storageKey: "attendance-monitor", all: EXPORT_HEADERS }}
            />
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-secondary/40 text-xs uppercase tracking-wider text-muted-foreground">
                <th className="p-3 text-left font-medium">Employee</th>
                <th className="p-3 text-left font-medium">Date</th>
                <th className="p-3 text-left font-medium">Check-in</th>
                <th className="p-3 text-left font-medium">Check-out</th>
                <th className="p-3 text-left font-medium">Hours</th>
                <th className="p-3 text-left font-medium">Site</th>
                <th className="p-3 text-left font-medium">GPS / Geofence</th>
                <th className="p-3 text-left font-medium">Status</th>
                <th className="p-3 text-left font-medium">Mode</th>
                <th className="p-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && records.length === 0 && (
                <tr><td colSpan={10} className="p-8 text-center text-muted-foreground">
                  <Loader2 className="mx-auto h-4 w-4 animate-spin" />
                </td></tr>
              )}
              {!loading && records.length === 0 && (
                <tr><td colSpan={10} className="p-12 text-center text-muted-foreground">
                  <FileText className="mx-auto mb-2 h-8 w-8 opacity-40" />
                  No attendance records match your filters.
                </td></tr>
              )}
              {records.map((r) => (
                <tr
                  key={r.id}
                  className="border-b last:border-0 cursor-pointer transition-colors hover:bg-secondary/30"
                  onClick={() => openDetail(r)}
                >
                  <td className="p-3">
                    <div className="flex items-center gap-2.5">
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-primary text-[10px] font-semibold text-white">
                        {r.employeeName.split(" ").map((p) => p[0]).slice(0, 2).join("")}
                      </div>
                      <div>
                        <div className="font-medium leading-tight">{r.employeeName}</div>
                        <div className="text-xs text-muted-foreground">
                          {r.employeeEmpCode}{r.employeeDepartment ? ` · ${r.employeeDepartment}` : ""}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-muted-foreground">{r.date}</td>
                  <td className="p-3 font-mono">{formatTime(r.checkInAt, tz) ?? <span className="text-muted-foreground">—</span>}</td>
                  <td className="p-3 font-mono">{formatTime(r.checkOutAt, tz) ?? <span className="text-muted-foreground">—</span>}</td>
                  <td className="p-3"><WorkedHours record={r} /></td>
                  <td className="p-3">
                    <div className="flex items-center gap-1.5">
                      <MapPin className="h-3 w-3 text-muted-foreground" />
                      <span className="text-xs">{r.siteName ?? "—"}</span>
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-1">
                      <Badge
                        variant="outline"
                        className={`text-[10px] ${r.gpsStatus === "VALID" ? "border-success/30 text-success" : r.gpsStatus === "INVALID" ? "border-destructive/30 text-destructive" : "border-border text-muted-foreground"}`}
                      >
                        GPS {GPS_LABELS[r.gpsStatus]}
                      </Badge>
                      {r.workMode !== "WFO" ? (
                        // Site geofence is "Outside" for a home or WFA check-in
                        // by design — show why it was allowed, not a red flag.
                        <Badge variant="outline" className="text-[10px] border-info/30 text-info bg-info/5">
                          {r.workMode === "WFH" ? "At home" : "Approved WFA"}
                        </Badge>
                      ) : (
                        <Badge
                          variant="outline"
                          className={`text-[10px] ${r.geofenceStatus === "INSIDE" ? "border-success/30 text-success" : r.geofenceStatus === "OUTSIDE" ? "border-destructive/30 text-destructive" : "border-border text-muted-foreground"}`}
                        >
                          {GEOFENCE_LABELS[r.geofenceStatus]}
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="p-3"><AttendanceStatusBadge status={STATUS_LABELS[r.status]} /></td>
                  <td className="p-3">
                    <Badge
                      variant="outline"
                      className={`text-[10px] ${r.workMode !== "WFO" ? "border-info/30 text-info bg-info/5" : "border-border text-muted-foreground"}`}
                    >
                      {r.workMode}
                    </Badge>
                  </td>
                  <td className="p-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <Button variant="ghost" size="icon" onClick={() => openDetail(r)}>
                      <Eye className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <ListPager page={page} pageSize={PAGE_SIZE} total={total} loading={loading} onPageChange={setPage} />
      </Card>

      {/* Detail sheet */}
      <Sheet
        open={!!active}
        onOpenChange={(o) => {
          if (flagging || o) return;
          setActive(null);
          setActiveDetail(null);
        }}
      >
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          {active && (
            <>
              <SheetHeader>
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-primary text-sm font-semibold text-white">
                    {active.employeeName.split(" ").map((p) => p[0]).slice(0, 2).join("")}
                  </div>
                  <div>
                    <SheetTitle className="text-left">{active.employeeName}</SheetTitle>
                    <SheetDescription className="text-left">
                      {active.employeeEmpCode}{active.employeeDepartment ? ` · ${active.employeeDepartment}` : ""}
                    </SheetDescription>
                  </div>
                  <Badge
                    variant="outline"
                    className={`ml-auto text-[10px] ${active.workMode !== "WFO" ? "border-info/30 text-info bg-info/5" : "border-border text-muted-foreground"}`}
                  >
                    {active.workMode}
                  </Badge>
                  <AttendanceStatusBadge status={STATUS_LABELS[active.status]} />
                </div>
              </SheetHeader>

              <div className="mt-6 space-y-5">
                <div className="grid grid-cols-2 gap-3">
                  <DetailStat icon={Clock} label="Check-in" value={formatTime(active.checkInAt, tz) ?? "—"} />
                  <DetailStat icon={Clock} label="Check-out" value={formatTime(active.checkOutAt, tz) ?? "—"} />
                  <HoursWorkedStat record={active} />
                  <DetailStat icon={User} label="Date" value={active.date} />
                  <DetailStat icon={Building} label="Site" value={active.siteName ?? "—"} />
                  <DetailStat
                    icon={Navigation}
                    label="GPS validation"
                    value={GPS_LABELS[active.gpsStatus]}
                    tone={active.gpsStatus === "VALID" ? "success" : active.gpsStatus === "INVALID" ? "destructive" : undefined}
                  />
                  <DetailStat
                    icon={MapPin}
                    label={active.workMode !== "WFO" ? "Work location" : "Geofence"}
                    value={
                      active.workMode === "WFH"
                        ? "At home"
                        : active.workMode === "WFA"
                          ? "Approved work from anywhere"
                          : GEOFENCE_LABELS[active.geofenceStatus]
                    }
                    tone={
                      active.workMode !== "WFO" ? "info" :
                      active.geofenceStatus === "INSIDE" ? "success" :
                      active.geofenceStatus === "OUTSIDE" ? "destructive" : undefined
                    }
                  />
                </div>

                <div>
                  <h4 className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Location map
                  </h4>
                  {activeLoading && (
                    <div className="flex h-56 items-center justify-center rounded-lg border border-dashed text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                    </div>
                  )}
                  {!activeLoading && activeDetail && (
                    <AttendanceMap
                      site={activeDetail.site}
                      sessions={activeDetail.sessions}
                      home={activeDetail.home}
                    />
                  )}
                </div>

                <div>
                  <div className="mb-3 flex items-center justify-between">
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Sessions
                    </h4>
                    {activeDetail && activeDetail.sessions.length > 0 && (
                      <span className="text-xs text-muted-foreground">
                        {activeDetail.sessions.length} session
                        {activeDetail.sessions.length === 1 ? "" : "s"}
                        {" · "}
                        {formatDuration(
                          activeDetail.sessions.reduce(
                            (sum, s) => sum + (s.durationMinutes ?? 0),
                            0,
                          ),
                        )}
                      </span>
                    )}
                  </div>

                  {activeLoading && (
                    <div className="flex justify-center py-6 text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                    </div>
                  )}

                  {!activeLoading && activeDetail && activeDetail.sessions.length === 0 && (
                    <div className="rounded-lg border border-dashed p-4 text-center text-xs text-muted-foreground">
                      No sessions recorded for this day.
                    </div>
                  )}

                  {!activeLoading && activeDetail && activeDetail.sessions.length > 0 && (
                    <ol className="space-y-3">
                      {activeDetail.sessions.map((s, i) => (
                        <SessionTimelineItem key={s.id} session={s} index={i} />
                      ))}
                    </ol>
                  )}
                </div>

                {active.flaggedReason && (
                  <div className="rounded-lg border-l-4 border-warning bg-warning/5 p-3">
                    <div className="flex gap-2 text-sm">
                      <AlertTriangle className="h-4 w-4 shrink-0 text-warning mt-0.5" />
                      <div>
                        <div className="font-medium">Flag reason</div>
                        <div className="mt-1 text-muted-foreground">{active.flaggedReason}</div>
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <Label className="text-xs">Flag reason (optional)</Label>
                  <Textarea
                    value={flagReason}
                    onChange={(e) => setFlagReason(e.target.value)}
                    placeholder="Why is this record being flagged for review?"
                    className="mt-1.5"
                    rows={3}
                  />
                </div>

                <div className="flex gap-2 pt-2">
                  <Button
                    className="flex-1 bg-gradient-primary"
                    onClick={submitFlag}
                    disabled={flagging}
                  >
                    {flagging ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" />Flagging…</> : <><Flag className="mr-1.5 h-4 w-4" />Flag for review</>}
                  </Button>
                  <Button variant="outline" onClick={() => setActive(null)} disabled={flagging}>Close</Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

function KpiCard({
  label, value, hint, tone,
}: { label: string; value: number; hint?: string; tone?: "primary" | "destructive" | "warning" }) {
  const toneCls =
    tone === "primary" ? "text-primary" :
    tone === "destructive" ? "text-destructive" :
    tone === "warning" ? "text-warning" :
    "text-success";
  return (
    <Card className="p-4">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`mt-1 text-2xl font-semibold ${toneCls}`}>{value}</div>
      {hint && <div className="text-[10px] text-muted-foreground">{hint}</div>}
    </Card>
  );
}

function DetailStat({
  icon: Icon, label, value, tone, detail, className,
}: {
  icon: typeof Clock;
  label: string;
  value: string;
  tone?: "success" | "destructive" | "info" | "warning";
  /** A second, smaller line under the value. */
  detail?: string;
  className?: string;
}) {
  const toneCls =
    tone === "success" ? "text-success" :
    tone === "destructive" ? "text-destructive" :
    tone === "info" ? "text-info" :
    tone === "warning" ? "text-warning" :
    "text-foreground";
  return (
    <div className={`rounded-lg border bg-secondary/30 p-3 ${className ?? ""}`}>
      <div className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className={`mt-1 text-sm font-semibold ${toneCls}`}>{value}</div>
      {detail && <div className={`mt-0.5 text-xs ${toneCls}`}>{detail}</div>}
    </div>
  );
}

function HoursWorkedStat({ record }: { record: AttendanceItem }) {
  const { value, detail, short } = hoursWorkedSummary(record);
  return (
    <DetailStat
      icon={Clock}
      label="Hours worked"
      className="col-span-2"
      value={value}
      detail={detail ?? undefined}
      tone={short ? "warning" : undefined}
    />
  );
}

const CLOSE_REASON_LABEL: Record<SessionCloseReason, string> = {
  EMPLOYEE: "Checked out",
  SITE_SWITCH: "Auto-closed (site switch)",
  ADMIN_CORRECTION: "Closed by admin correction",
};

function SessionTimelineItem({
  session,
  index,
}: {
  session: AttendanceSessionItem;
  index: number;
}) {
  const tz = useTenantTimezone();
  const open = session.endAt == null;
  const start = formatTime(session.startAt, tz) ?? "—";
  const end = session.endAt ? formatTime(session.endAt, tz) : null;
  const wfh = session.workMode === "WFH";
  return (
    <li className="rounded-lg border p-3 text-sm">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 font-medium">
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary/10 text-[10px] font-semibold text-primary">
            {index + 1}
          </span>
          <span>
            {start} {end ? `→ ${end}` : "→ now"}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          <Badge
            variant="outline"
            className={`text-[10px] ${wfh ? "border-info/30 text-info bg-info/5" : "border-border text-muted-foreground"}`}
          >
            {session.workMode}
          </Badge>
          {/* Provenance (FEAT-2026-013). Only shown for WEB: a session with no
              source predates tracking, and labelling those "Mobile" would
              assert something never recorded. */}
          {session.startSource === "WEB" && (
            <Badge variant="outline" className="text-[10px] border-border text-muted-foreground">
              Web
            </Badge>
          )}
          {/* A coarse fix cannot place someone inside a geofence (BR-152), so
              the reason a punch reads OUTSIDE may be accuracy, not location. */}
          {session.startGpsStatus === "INVALID" && (
            <Badge variant="outline" className="text-[10px] border-warning/30 text-warning bg-warning/5">
              {session.startAccuracyM != null
                ? `Low accuracy ±${session.startAccuracyM} m`
                : "Low accuracy"}
            </Badge>
          )}
          <Badge variant={open ? "default" : "secondary"} className="text-[10px]">
            {open ? "Open" : formatDuration(session.durationMinutes ?? 0)}
          </Badge>
        </div>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-muted-foreground">
        <span>
          Start geofence:{" "}
          <span className="text-foreground">
            {wfh
              ? "At home"
              : session.startGeofenceStatus === "INSIDE"
                ? "Inside"
                : session.startGeofenceStatus === "OUTSIDE"
                  ? "Outside"
                  : "N/A"}
          </span>
        </span>
        {session.endAt && (
          <span>
            End geofence:{" "}
            <span className="text-foreground">
              {wfh
                ? "At home"
                : session.endGeofenceStatus === "INSIDE"
                  ? "Inside"
                  : session.endGeofenceStatus === "OUTSIDE"
                    ? "Outside"
                  : "N/A"}
            </span>
          </span>
        )}
        {session.closedBy && (
          <span className="col-span-2">
            Closed by:{" "}
            <span className="text-foreground">{CLOSE_REASON_LABEL[session.closedBy]}</span>
          </span>
        )}
        {session.notes && (
          <span className="col-span-2">
            Notes: <span className="text-foreground">{session.notes}</span>
          </span>
        )}
      </div>
    </li>
  );
}

export default Attendance;
